'use client';

import { ElementRef, HTMLAttributes } from 'react';
import type { ComponentPropsWithoutRef } from 'react';
import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';

import { cn } from '@/lib/utils';
import { DIALOG_VIEWPORT_FIT } from '@/components/ui/dialog-viewport';
import {
  NESTED_OVERLAY_Z,
  PageOverlayScope,
  useInsidePageOverlay
} from '@/components/ui/page-overlay-scope';

/*
 * A dialog opened from inside a page-covering overlay has to paint above it.
 * The rung itself lives on `NESTED_OVERLAY_Z` beside the context that decides
 * when it applies, because `Sheet` needs the same answer and a second copy here
 * is how the two drifted apart in the first place (VALE-1802). Read from context
 * rather than passed, for the same reason menu modality is (VALE-1418): a prop
 * is one more thing each call site can get wrong.
 */

const Dialog = DialogPrimitive.Root;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

const DialogOverlay = ({
  ref,
  className,
  ...props
}: ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay> & {
  ref?: React.RefObject<ElementRef<typeof DialogPrimitive.Overlay> | null>;
}) => {
  const nested = useInsidePageOverlay();
  return (
    <DialogPrimitive.Overlay
      ref={ref}
      className={cn(
        'bg-overlay fixed inset-0 z-50',
        nested && NESTED_OVERLAY_Z,
        'data-[state=open]:animate-in data-[state=closed]:animate-out',
        'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
        className
      )}
      {...props}
    />
  );
};
DialogOverlay.displayName =
  DialogPrimitive.Overlay.displayName || 'DialogOverlay';

const DialogContent = ({
  ref,
  className,
  children,
  hideCloseButton,
  ...props
}: ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
  hideCloseButton?: boolean;
} & {
  ref?: React.RefObject<ElementRef<typeof DialogPrimitive.Content> | null>;
}) => {
  const nested = useInsidePageOverlay();
  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          'bg-popover border-border-strong fixed top-[50%] left-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border p-6 shadow-lg duration-200 sm:rounded-lg',
          nested && NESTED_OVERLAY_Z,
          DIALOG_VIEWPORT_FIT,
          'data-[state=open]:animate-in data-[state=closed]:animate-out',
          'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
          'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
          className
        )}
        {...props}
      >
        <PageOverlayScope>
          {children}
          {!hideCloseButton && (
            <DialogPrimitive.Close className="focus:ring-ring/70 data-[state=open]:bg-accent data-[state=open]:text-muted-foreground absolute top-4 right-4 cursor-pointer rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:outline-hidden disabled:pointer-events-none">
              <X className="h-4 w-4" />
              <span className="sr-only">Close</span>
            </DialogPrimitive.Close>
          )}
        </PageOverlayScope>
      </DialogPrimitive.Content>
    </DialogPortal>
  );
};
DialogContent.displayName =
  DialogPrimitive.Content.displayName || 'DialogContent';

const DialogHeader = ({
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'flex flex-col space-y-1.5 text-center sm:text-left',
      className
    )}
    {...props}
  />
);
DialogHeader.displayName = 'DialogHeader';

const DialogFooter = ({
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2',
      className
    )}
    {...props}
  />
);
DialogFooter.displayName = 'DialogFooter';

const DialogTitle = ({
  ref,
  className,
  ...props
}: ComponentPropsWithoutRef<typeof DialogPrimitive.Title> & {
  ref?: React.RefObject<ElementRef<typeof DialogPrimitive.Title> | null>;
}) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      'text-lg leading-none font-semibold tracking-tight',
      className
    )}
    {...props}
  />
);
DialogTitle.displayName = DialogPrimitive.Title.displayName || 'DialogTitle';

const DialogDescription = ({
  ref,
  className,
  ...props
}: ComponentPropsWithoutRef<typeof DialogPrimitive.Description> & {
  ref?: React.RefObject<ElementRef<typeof DialogPrimitive.Description> | null>;
}) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn('text-muted-foreground text-sm', className)}
    {...props}
  />
);
DialogDescription.displayName =
  DialogPrimitive.Description.displayName || 'DialogDescription';

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogClose,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription
};
